// src/context/AuthContext.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from './NotificationContext';
import { AuthService } from '../services/auth.service';
import { http } from '../api/http';
import { isSessionIdleSuppressed, SESSION_ACTIVITY_EVENT } from '../utils/sessionActivity';

export type UserRole = 'client' | 'admin' | 'manager' | 'developer' | 'guest' | (string & {});

export interface User {
  id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  role: UserRole;
  permissions?: string[];
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  state?: string;
  postal_code?: string;
  date_of_birth?: string;
  nationality?: string;
  rfc?: string;
  bio?: string;
  memberSince: string;
}

type MFAPending =
  | { type: 'login'; mfaToken: string; email: string }
  | { type: 'register'; setupToken: string; email: string };

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;
  profileComplete: boolean;
  profileStatus: any | null;
  login: (email: string, password: string) => Promise<void>;
  logout: (opts?: { silent?: boolean; reason?: 'LOGOUT' | 'EXPIRED'; notifyServer?: boolean }) => void;
  refreshMe: () => Promise<void>;
  checkProfileStatus: (force?: boolean) => Promise<void>;
  getMfaPending: () => MFAPending | null;
  clearMfaPending: () => void;
  verifyLoginMfa: (code: string) => Promise<void>;
  confirmRegisterMfa: (code: string) => Promise<void>;
}

const AUTH_USER_KEY = 'auth_user';
const AUTH_TOKEN_KEY = 'auth_token';
const MFA_PENDING_KEY = 'mfa_pending';
const LAST_ACTIVITY_KEY = 'last_activity_ts';

const getDefaultRedirect = (role?: UserRole) => {
  if (!role) return '/dashboard';
  if (role === 'admin' || role === 'manager') return '/admin/dashboard';
  if (role === 'developer') return '/developer/panel';
  return '/dashboard';
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  return ctx;
};

function safeParseUser(raw: string | null): User | null {
  if (!raw) return null;
  try {
    const u = JSON.parse(raw) as Partial<User>;
    if (!u.email || !u.role || !u.memberSince) return null;
    return u as User;
  } catch {
    return null;
  }
}

function safeParseMfa(raw: string | null): MFAPending | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as MFAPending;
    if (!v || typeof v !== 'object') return null;
    if ((v as any).type === 'login' && (v as any).mfaToken && (v as any).email) return v;
    if ((v as any).type === 'register' && (v as any).setupToken && (v as any).email) return v;
    return null;
  } catch {
    return null;
  }
}

type LoginOk = { user: User; token: string };
type LoginMFA = { mfaRequired: true; mfaToken: string };

type RegisterOk = { user: User; token: string };
type RegisterMFA = { mfaSetupRequired: true; setupToken: string };

function isLoginMFA(res: LoginOk | LoginMFA): res is LoginMFA {
  return 'mfaRequired' in res && res.mfaRequired === true;
}

function isRegisterMFA(res: RegisterOk | RegisterMFA): res is RegisterMFA {
  return 'mfaSetupRequired' in res && res.mfaSetupRequired === true;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState<any>(null);
  const lastProfileCheckRef = useRef(0);
  const idleTimerRef = useRef<number | null>(null);
  const heartbeatRef = useRef<number | null>(null);

  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const IDLE_TIMEOUT_MS = 3 * 60 * 1000;

  const clearSession = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  };

  const setSession = (nextUser: User, nextToken: string) => {
    setUser(nextUser);
    setToken(nextToken);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(nextUser));
    localStorage.setItem(AUTH_TOKEN_KEY, nextToken);
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  };

  const logout = (opts?: { silent?: boolean; reason?: 'LOGOUT' | 'EXPIRED'; notifyServer?: boolean }) => {
    const reason = opts?.reason || 'LOGOUT';
    const notifyServer = opts?.notifyServer !== false;
    const tokenSnapshot = localStorage.getItem(AUTH_TOKEN_KEY);
    if (notifyServer && tokenSnapshot) {
      AuthService.logout(reason).catch(() => {});
    }
    clearSession();
    localStorage.removeItem(MFA_PENDING_KEY);
    navigate('/login', { replace: true });
    if (!opts?.silent) addNotification('Sesión cerrada correctamente.', 'info');
  };

  const getMfaPending = () => safeParseMfa(localStorage.getItem(MFA_PENDING_KEY));

  const clearMfaPending = () => {
    localStorage.removeItem(MFA_PENDING_KEY);
  };

  const refreshMe = async () => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token && !storedToken) return;
    try {
      const me = await http<User>('/me');
      setUser(me);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(me));
      
      // También verificar estado del perfil
      await checkProfileStatus();
    } catch (error: any) {
      const status = error?.status ?? error?.payload?.statusCode;
      if (status === 401) logout({ silent: true, notifyServer: false });
    }
  };

  const checkProfileStatus = useCallback(async (force = false) => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token && !storedToken) return;
    const now = Date.now();
    if (!force && now - lastProfileCheckRef.current < 15000) return; // throttle 15s
    lastProfileCheckRef.current = now;
    try {
      const status = await http('/me/profile/status');
      setProfileStatus(status);
    } catch (error) {
      console.error('Error checking profile status:', error);
      setProfileStatus(null);
    }
  }, [token]);

  useEffect(() => {
    const savedUser = safeParseUser(localStorage.getItem(AUTH_USER_KEY));
    const savedToken = localStorage.getItem(AUTH_TOKEN_KEY);

    if (savedUser && savedToken) {
      setUser(savedUser);
      setToken(savedToken);
      
      // Verificar estado del perfil al cargar
      checkProfileStatus(true);

      // Refrescar datos del usuario para evitar datos ofuscados en cache
      refreshMe();
    } else {
      clearSession();
    }

    setIsLoading(false);
  }, [checkProfileStatus]);

  useEffect(() => {
    const onUnauthorized = (event: Event) => {
      if (!user) return;
      const detail = (event as CustomEvent<any>).detail || {};
      const code = detail?.code;
      const message =
        detail?.message ||
        (code === 'SESSION_INVALIDATED'
          ? 'Tu sesión se cerró porque se inició sesión desde otro dispositivo.'
          : code === 'SESSION_EXPIRED'
            ? 'Tu sesión expiró por inactividad.'
            : 'Tu sesión expiró. Inicia sesión nuevamente.');

      logout({ silent: true, notifyServer: false });
      addNotification(message, 'warning');
    };

    window.addEventListener('auth:unauthorized', onUnauthorized as EventListener);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized as EventListener);
  }, [addNotification, logout, user]);

  useEffect(() => {
    if (!user) {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    const updateLastActivity = () => {
      const now = Date.now();
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      return now;
    };

    const getLastActivity = () => {
      const stored = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
      return stored || Date.now();
    };

    const schedule = () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => {
        if (isSessionIdleSuppressed()) {
          updateLastActivity();
          schedule();
          return;
        }
        logout({ silent: true, reason: 'EXPIRED' });
        addNotification('Tu sesión ha expirado por inactividad. Por favor inicia sesión nuevamente.', 'warning');
      }, IDLE_TIMEOUT_MS);
    };

    const handleActivity = () => {
      if (isSessionIdleSuppressed()) {
        updateLastActivity();
        schedule();
        return;
      }
      const last = getLastActivity();
      const now = Date.now();
      if (now - last >= IDLE_TIMEOUT_MS) {
        logout({ silent: true, reason: 'EXPIRED' });
        addNotification('Tu sesión ha expirado por inactividad. Por favor inicia sesión nuevamente.', 'warning');
        return;
      }
      updateLastActivity();
      schedule();
    };

    const handleResume = () => {
      if (isSessionIdleSuppressed()) {
        updateLastActivity();
        schedule();
        return;
      }
      const last = getLastActivity();
      const now = Date.now();
      if (now - last >= IDLE_TIMEOUT_MS) {
        logout({ silent: true, reason: 'EXPIRED' });
        addNotification('Tu sesión ha expirado por inactividad. Por favor inicia sesión nuevamente.', 'warning');
        return;
      }
      schedule();
    };

    const keepAliveIfNeeded = () => {
      if (!isSessionIdleSuppressed()) return;
      updateLastActivity();
      schedule();
      http('/me').catch(() => {});
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'input', 'change', 'focusin'];
    events.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    window.addEventListener(SESSION_ACTIVITY_EVENT, handleActivity);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') handleResume();
    };

    window.addEventListener('focus', handleResume);
    document.addEventListener('visibilitychange', handleVisibility);
    updateLastActivity();
    schedule();
    heartbeatRef.current = window.setInterval(keepAliveIfNeeded, 60 * 1000);

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handleActivity));
      window.removeEventListener(SESSION_ACTIVITY_EVENT, handleActivity);
      window.removeEventListener('focus', handleResume);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      if (heartbeatRef.current) window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    };
  }, [addNotification, logout, user]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const res = (await AuthService.login(email, password)) as LoginOk | LoginMFA;

      if (isLoginMFA(res)) {
        localStorage.setItem(MFA_PENDING_KEY, JSON.stringify({ type: 'login', mfaToken: res.mfaToken, email }));
        navigate('/mfa/verify', { replace: true });
        addNotification('Te enviamos un código a tu correo.', 'info');
        return;
      }

      setSession(res.user, res.token);
      clearMfaPending();
      
      // Verificar estado del perfil después del login
      await checkProfileStatus();

      navigate(getDefaultRedirect(res.user.role), { replace: true });
      addNotification('Inicio de sesión exitoso.', 'success');
    } catch (error: any) {
      let errorMessage = 'Credenciales incorrectas.';

      if (error?.payload?.message) errorMessage = error.payload.message;
      else if (error?.message) errorMessage = error.message;

      // Si el error es específico de cuenta pendiente, redirigir a pending approval
      if (errorMessage.includes('pendiente de aprobación') || errorMessage.includes('pending_approval')) {
        navigate('/pending-approval', { replace: true });
        addNotification('Tu cuenta está pendiente de aprobación.', 'info');
        return;
      }
      
      // Si el error es de cuenta rechazada, redirigir a página de rechazo
      if (errorMessage.includes('rechazada') || errorMessage.includes('rejected')) {
        navigate('/account-rejected', { replace: true });
        addNotification('Tu cuenta ha sido rechazada.', 'error');
        return;
      }

      addNotification(errorMessage, 'error');
      clearSession();
      clearMfaPending();
    } finally {
      setIsLoading(false);
    }
  };

  const verifyLoginMfa = async (code: string) => {
    const pending = getMfaPending();
    if (!pending || pending.type !== 'login') {
      addNotification('No hay una verificación MFA pendiente.', 'error');
      navigate('/login', { replace: true });
      return;
    }

    setIsLoading(true);
    try {
      const res = await AuthService.verifyMfa(pending.mfaToken, code.trim());
      setSession(res.user, res.token);
      clearMfaPending();
      
      // Verificar estado del perfil después de la verificación MFA
      await checkProfileStatus();
      await refreshMe();

      navigate(getDefaultRedirect(res.user.role), { replace: true });

      addNotification('Verificación exitosa.', 'success');
    } catch (error: any) {
      let errorMessage = 'Código inválido o expirado.';

      if (error?.payload?.message) errorMessage = error.payload.message;
      else if (error?.message) errorMessage = error.message;

      addNotification(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmRegisterMfa = async (code: string) => {
    const pending = getMfaPending();
    if (!pending || pending.type !== 'register') {
      addNotification('No hay una verificación pendiente.', 'error');
      navigate('/registro', { replace: true });
      return;
    }

    setIsLoading(true);
    try {
      await AuthService.confirmMfaSetup(pending.setupToken, code.trim());
      clearMfaPending();
      
      // Para registro, NO hacer login automático, ir directo a pending approval
      addNotification('Verificación exitosa. Tu cuenta está pendiente de aprobación.', 'success');
      navigate('/pending-approval', { replace: true });
    } catch (error: any) {
      let errorMessage = 'Código inválido o expirado.';

      if (error?.payload?.message) errorMessage = error.payload.message;
      else if (error?.message) errorMessage = error.message;

      addNotification(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const isAuthenticated = !!user && !!token;
  const profileComplete = profileStatus?.profile_completed || false;

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated,
      user,
      token,
      isLoading,
      profileComplete,
      profileStatus,
      login,
      logout,
      refreshMe,
      checkProfileStatus,
      getMfaPending,
      clearMfaPending,
      verifyLoginMfa,
      confirmRegisterMfa
    }),
    [isAuthenticated, user, token, isLoading, profileComplete, profileStatus, checkProfileStatus]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
